import { session } from "./storage";
import { confirmLeave } from "./leave-guard";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useApi } from "./hooks";
import type { Client } from "./types";
import { clientIdPage } from "./sidebar-mode";
const Context = createContext<ReturnType<typeof useSelection> | null>(null);
function useSelection() {
  const clients = useApi<Client[]>("/api/clients");
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const [selected, setSelected] = useState(
    () => session.get("nl-aos-client") || "",
  );
  // On a profile or ads report (`/clients/<id>`, `/ads/<id>`) the path names
  // the client, so the sidebar header must follow it rather than whatever was
  // picked before.
  const requested =
    clientIdPage(location.pathname)?.id || params.get("client_id") || selected;
  // Trust the id the URL or session already names while the client list loads,
  // so a client page's own request starts at once instead of waiting a round
  // trip for /api/clients. Once the list arrives it validates the choice.
  const clientId = clients.data
    ? (clients.data.find((c) => String(c.id) === requested)?.id ?? clients.data[0]?.id ?? null)
    : /^\d+$/.test(requested) ? Number(requested) : null;
  useEffect(() => {
    if (clientId) {
      setSelected(String(clientId));
      session.set("nl-aos-client", String(clientId));
    }
  }, [clientId]);
  /** `keep`: open records that belong to the new client, such as an SOP run moving the sidebar to its own client. */
  const setClientId = (id: number, keep: string[] = []) => {
    if (id !== clientId && !confirmLeave()) return;
    setSelected(String(id));
    session.set("nl-aos-client", String(id));
    const next = new URLSearchParams(params);
    next.set("client_id", String(id));
    // A record opened for the previous client must not follow the switch.
    for (const key of ["record", "report", "session", "task", "run"]) if (!keep.includes(key)) next.delete(key);
    setParams(next);
  };
  return {
    clients,
    clientId,
    setClientId,
    client: clients.data?.find((c) => c.id === clientId),
    location,
  };
}
export function ClientProvider({ children }: { children: ReactNode }) {
  const value = useSelection();
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
export function useClientSelection() {
  const value = useContext(Context);
  if (!value) throw Error("ClientProvider missing");
  return value;
}
